/**
 * The content type to declare for `file`. Presigned URLs sign it, so the value
 * sent when requesting the URL and the PUT header must be identical.
 */
export function uploadContentType(file: File): string {
  return file.type || "application/octet-stream";
}

/**
 * Upload a file directly to a presigned S3 URL.
 *
 * We use `XMLHttpRequest` rather than axios/fetch for two reasons:
 *  - axios attaches headers that trigger a CORS preflight the S3
 *    presigned-PUT URL doesn't permit, so the upload fails.
 *  - XHR exposes upload-progress events, which we surface to the UI.
 *
 * Resolves once S3 returns a 2xx; rejects on a non-2xx status or a
 * network/transport error.
 */
export function uploadFileWithXHR({
  file,
  presignedUrl,
  onProgress,
}: {
  file: File;
  presignedUrl: string;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    xhr.addEventListener("error", () =>
      reject(new Error("Upload failed due to a network error")),
    );

    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", uploadContentType(file));
    xhr.send(file);
  });
}
